import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { lobSchema, LOBFormData } from '../schemas/master-data.schemas';
import { LOB } from '../types/master-data.types';
import { LOBLevel } from '@/types';
import { Loader2 } from 'lucide-react';

interface LOBFormProps {
  lob?: LOB;
  lobs: LOB[];
  presetParent?: LOB;
  onSubmit: (data: LOBFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function LOBForm({ lob, lobs, presetParent, onSubmit, onCancel, isLoading }: LOBFormProps) {
  const parentLevel = presetParent
    ? typeof presetParent.level === 'number'
      ? presetParent.level
      : Number(presetParent.level)
    : null;
  const childLevelEnum =
    parentLevel === 1 ? LOBLevel.VERTICAL : parentLevel === 2 ? LOBLevel.PRODUCT : LOBLevel.SECTOR;
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<LOBFormData>({
    resolver: zodResolver(lobSchema),
    defaultValues: lob
      ? {
          name: lob.name,
          code: lob.code,
          level: lob.level,
          parentId: lob.parentId || undefined,
          description: lob.description || '',
          targetMarginPercentage: lob.targetMarginPercentage ?? undefined,
          floorMarginPercentage: lob.floorMarginPercentage ?? undefined,
          pctGarantia: (lob as any).pctGarantia ?? undefined,
          pctNacionalizacion: (lob as any).pctNacionalizacion ?? undefined,
          tipoFlete: (lob as any).tipoFlete ?? undefined,
          pctFlete: (lob as any).pctFlete ?? undefined,
          sapCode: (lob as any).sapCode ?? undefined,
        }
      : {
          name: '',
          code: '',
          level: presetParent ? childLevelEnum : LOBLevel.SECTOR,
          parentId: presetParent?.id,
          description: '',
          targetMarginPercentage: undefined,
          floorMarginPercentage: undefined,
          pctGarantia: undefined,
          pctNacionalizacion: undefined,
          tipoFlete: undefined,
          pctFlete: undefined,
          sapCode: undefined,
        },
  });

  const selectedLevel = watch('level');
  const selectedParentId = watch('parentId');

  // Filter parent options based on level
  const getParentOptions = () => {
    if (selectedLevel === LOBLevel.SECTOR) {
      return []; // Sectors have no parent
    }
    if (selectedLevel === LOBLevel.VERTICAL) {
      return lobs.filter((l) => l.level === LOBLevel.SECTOR);
    }
    if (selectedLevel === LOBLevel.PRODUCT) {
      return lobs.filter((l) => l.level === LOBLevel.VERTICAL);
    }
    return [];
  };

  const parentOptions = getParentOptions();

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {presetParent && !lob && (
        <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
          Creando <strong>{parentLevel === 1 ? 'Vertical' : 'Marca'}</strong> dentro de{' '}
          <strong>{presetParent.name}</strong>{' '}
          <span className="text-xs text-muted-foreground">({presetParent.code})</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nombre *</Label>
          <Input
            id="name"
            placeholder="Tecnología"
            {...register('name')}
            error={errors.name?.message}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="code">Código *</Label>
          <Input
            id="code"
            placeholder="TECH"
            {...register('code')}
            error={errors.code?.message}
            disabled={isLoading || !!lob}
            className="uppercase"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="level">Nivel *</Label>
        <Select
          value={selectedLevel}
          onValueChange={(value) => {
            setValue('level', value as LOBLevel);
            setValue('parentId', undefined); // Reset parent when level changes
          }}
          disabled={isLoading || !!lob}
        >
          <SelectTrigger id="level">
            <SelectValue placeholder="Selecciona un nivel" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={LOBLevel.SECTOR}>Sector</SelectItem>
            <SelectItem value={LOBLevel.VERTICAL}>Vertical</SelectItem>
            <SelectItem value={LOBLevel.PRODUCT}>Marca</SelectItem>
          </SelectContent>
        </Select>
        {errors.level && <p className="text-sm text-destructive">{errors.level.message}</p>}
        <p className="text-xs text-muted-foreground">
          {selectedLevel === LOBLevel.SECTOR && 'Nivel más alto de la jerarquía'}
          {selectedLevel === LOBLevel.VERTICAL && 'Debe pertenecer a un Sector'}
          {selectedLevel === LOBLevel.PRODUCT && 'Debe pertenecer a una Vertical'}
        </p>
      </div>

      {parentOptions.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="parentId">
            {selectedLevel === LOBLevel.VERTICAL ? 'Sector Padre *' : 'Vertical Padre *'}
          </Label>
          <Select
            value={selectedParentId}
            onValueChange={(value) => setValue('parentId', value)}
            disabled={isLoading}
          >
            <SelectTrigger id="parentId">
              <SelectValue placeholder="Selecciona el padre" />
            </SelectTrigger>
            <SelectContent>
              {parentOptions.map((parent) => (
                <SelectItem key={parent.id} value={parent.id}>
                  {parent.name} ({parent.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.parentId && <p className="text-sm text-destructive">{errors.parentId.message}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="targetMarginPercentage">Margen Objetivo (%)</Label>
          <Input
            id="targetMarginPercentage"
            type="number"
            step="0.1"
            min="0"
            max="100"
            placeholder="Ej: 25 (opcional)"
            {...register('targetMarginPercentage')}
            error={errors.targetMarginPercentage?.message}
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">
            Si se configura, sobreescribe el margen objetivo del modelo de precios para cotizaciones
            de este LOB.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="floorMarginPercentage">Margen Piso (%)</Label>
          <Input
            id="floorMarginPercentage"
            type="number"
            step="0.1"
            min="0"
            max="100"
            placeholder="Ej: 15 (opcional)"
            {...register('floorMarginPercentage')}
            error={errors.floorMarginPercentage?.message}
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">
            Si se configura, sobreescribe el margen piso del modelo de precios para cotizaciones de
            este LOB.
          </p>
        </div>
      </div>

      {(selectedLevel === LOBLevel.PRODUCT ||
        String(selectedLevel) === 'PRODUCT' ||
        Number(selectedLevel) === 3) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-blue-200 bg-blue-50/30 rounded-md">
          <div className="space-y-2">
            <Label htmlFor="pctGarantia">% Garantía / año (Pricer Datec)</Label>
            <Input
              id="pctGarantia"
              type="number"
              step="0.01"
              min="0"
              max="100"
              placeholder="Ej: 2.0 (opcional)"
              {...register('pctGarantia', {
                setValueAs: (v) => (v === '' || v == null ? null : parseFloat(v)),
              })}
              error={(errors as any).pctGarantia?.message}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              % anual de previsión por garantía aplicado al item cuando se elige esta marca en el
              Pricer Datec.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pctNacionalizacion">% Nacionalización (Pricer Datec)</Label>
            <Input
              id="pctNacionalizacion"
              type="number"
              step="0.01"
              min="0"
              max="100"
              placeholder="Ej: 1.9 (opcional)"
              {...register('pctNacionalizacion', {
                setValueAs: (v) => (v === '' || v == null ? null : parseFloat(v)),
              })}
              error={(errors as any).pctNacionalizacion?.message}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              % de nacionalización aplicado sobre el CIF cuando se elige esta marca en el Pricer
              Datec.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tipoFlete">Tipo de flete (Pricer Datec)</Label>
            <select
              id="tipoFlete"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              {...register('tipoFlete', {
                setValueAs: (v) => (v === '' || v == null ? null : v),
              })}
              disabled={isLoading}
            >
              <option value="">— Sin parametrizar —</option>
              <option value="NINGUNO">NINGUNO</option>
              <option value="AEREO">FLETE AÉREO</option>
              <option value="MARITIMO">FLETE MARÍTIMO</option>
              <option value="TERRESTRE">FLETE TERRESTRE</option>
              {/* Legacy: siguen siendo válidos en marcas ya parametrizadas */}
              <option value="AEREO_1">FLETE AÉREO 1 (legacy)</option>
              <option value="AEREO_2">FLETE AÉREO 2 (legacy)</option>
              <option value="MARITIMO_1">FLETE MARÍTIMO Y/O TERRESTRE 1 (legacy)</option>
              <option value="MARITIMO_2">FLETE MARÍTIMO Y/O TERRESTRE 2 (legacy)</option>
              <option value="MANUAL_1">FLETE MANUAL 1 (legacy)</option>
              <option value="MANUAL_2">FLETE MANUAL 2 (legacy)</option>
              <option value="FIJO_NWS">FLETE FIJO NWS/CSS (legacy)</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Tipo de flete que se aplica al ítem cuando se elige esta marca. Sin parametrizar, el
              ítem queda en NINGUNO y la cotización avisa.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pctFlete">% Flete propio (Pricer Datec)</Label>
            <Input
              id="pctFlete"
              type="number"
              step="0.01"
              min="0"
              max="100"
              placeholder="Opcional — pisa al % del tipo"
              {...register('pctFlete', {
                setValueAs: (v) => (v === '' || v == null ? null : parseFloat(v)),
              })}
              error={(errors as any).pctFlete?.message}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Solo si esta marca cotiza distinto del estándar de su tipo. Vacío = se usa el % del
              tipo de flete.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="description">Descripción</Label>
        <Textarea
          id="description"
          placeholder="Describe este LOB..."
          rows={3}
          {...register('description')}
          disabled={isLoading}
        />
        {errors.description && (
          <p className="text-sm text-destructive">{errors.description.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="sapCode">Código SAP</Label>
        <Input
          id="sapCode"
          placeholder="Código equivalente en SAP (opcional)"
          maxLength={64}
          {...register('sapCode')}
          error={(errors as any).sapCode?.message}
          disabled={isLoading}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : lob ? (
            'Actualizar'
          ) : (
            'Crear'
          )}
        </Button>
      </div>
    </form>
  );
}

export default LOBForm;
